<script setup lang="ts">
import { type ComponentPublicInstance, ref } from 'vue'
import promptsHttpService from '@/services/http/promptsHttpService.ts'
import type { PromptModel } from '@/services/http/httpResponses.ts'
import {
  getPromptTypeName,
  getTestStageName,
  type PromptType,
  Roles,
  type TestStage,
} from '@/constants.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const editPromptDialogOpen = ref(false)
const preRenderedPromptDialogOpen = ref(false)
const selectedPrompt = ref<PromptModel | null>(null)
const newContent = ref('')
const exampleData = ref<Record<string, unknown>>({})
const contentTextarea = ref<ComponentPublicInstance | null>(null)
const preRenderedPrompt = ref('')

function preprocessResponse(data: Record<string, unknown>): Record<string, unknown> {
  const parsed: Record<string, unknown> = { ...data }

  for (const [key, value] of Object.entries(data)) {
    if (key.endsWith('schema') && typeof value === 'string') {
      try {
        parsed[key] = JSON.parse(value)
      } catch (err) {
        console.warn(`Error parsing "${key}":`, err)
        parsed[key] = null
      }
    }
  }

  return parsed
}

promptsHttpService.getExampleData().then((x) => {
  exampleData.value = preprocessResponse(x)
})

const getPrompts = async () => {
  const promptsResponse = await promptsHttpService.getPrompts()
  return promptsResponse.prompts.map((data) => ({
    ...data,
    dateBegin: new Date(data.dateBegin),
    dateEnd: data.dateEnd ? new Date(data.dateEnd) : null,
  }))
}

const prompts = ref(await getPrompts())

const getPromptTypeLabel = (type: PromptType) => {
  return getPromptTypeName(type)
}
const getTestStageLabel = (stage: TestStage) => {
  return getTestStageName(stage)
}
const promptEditClicked = (prompt: PromptModel) => {
  selectedPrompt.value = prompt
  newContent.value = prompt.content
  editPromptDialogOpen.value = true
}
const hideDialog = () => {
  selectedPrompt.value = null
  newContent.value = ''
  editPromptDialogOpen.value = false
}
const savePrompt = async () => {
  const isPromptCorrect = await preRenderPrompt('validation')

  if (!isPromptCorrect) {
    return
  }

  const response = await promptsHttpService.updatePrompt(selectedPrompt.value!.id, newContent.value)

  if (response.status !== 200) {
    alert('Ошибка при сохранении')
    return
  }

  prompts.value = await getPrompts()
  editPromptDialogOpen.value = false
}

const preRenderPrompt = async (intention: 'validation' | 'preview'): Promise<boolean> => {
  const response = await promptsHttpService.preRenderPrompt(newContent.value)

  if (response.status === 200 && response.data.result != null) {
    if (intention === 'preview') {
      preRenderedPrompt.value = response.data.result
      preRenderedPromptDialogOpen.value = true
    }
    return true
  }

  if (response.status !== 200 && response.data.errors != null) {
    alert(response.data.errors.join('\n'))
    return false
  }

  alert('Неизвестная ошибка')

  return false
}

const insertText = (text: string) => {
  const textarea = contentTextarea.value?.$el as HTMLTextAreaElement | null

  if (!textarea) {
    return
  }

  textarea.focus()

  const successful = document.execCommand('insertText', false, text)

  if (!successful) {
    textarea.setRangeText(text, textarea.selectionStart, textarea.selectionEnd, 'end')
    newContent.value = textarea.value
  }
}

const propertyClicked = (field: string) => {
  insertText('{{' + field + '}}')
}
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <DataTable ref="dt" :value="prompts" dataKey="id">
      <template #header>
        <div class="flex flex-wrap gap-2 items-center justify-between">
          <h4 class="m-0">Prompts</h4>
        </div>
      </template>

      <Column header="Тип" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getPromptTypeLabel(slotProps.data.type) }}
        </template>
      </Column>
      <Column header="Стадия" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getTestStageLabel(slotProps.data.testStage) }}
        </template>
      </Column>
      <Column header="Контент" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.content }}
        </template>
      </Column>
      <Column :exportable="false" style="min-width: 12rem">
        <template #body="slotProps">
          <Button
            icon="pi pi-pencil"
            outlined
            class="mr-2"
            @click="promptEditClicked(slotProps.data)"
          />
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
    </DataTable>
  </div>

  <Dialog
    v-model:visible="editPromptDialogOpen"
    class="max-w-[80vw] min-h-[90vh] w-full"
    header="Редактирование prompt-a"
    :modal="true"
    :closeOnEscape="false"
    content-class="h-full w-full flex grow"
  >
    <div class="flex grow flex-col gap-6">
      <div>
        <h2 class="mb-2">Возможные переменные:</h2>
        <div class="flex gap-x-1 gap-y-2 flex-wrap">
          <Chip
            class="cursor-pointer"
            v-for="field in Object.keys(exampleData)"
            :key="field"
            @click="propertyClicked(field)"
          >
            {{ field }}
          </Chip>
        </div>
      </div>
      <div class="max-w-[78vw]">
        <Accordion>
          <AccordionPanel value="0">
            <AccordionHeader>Пример объекта:</AccordionHeader>
            <AccordionContent>
              <pre class="m-0 max-w-[78vw] whitespace-pre-wrap break-words">{{ exampleData }}</pre>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
      <div class="w-full h-full">
        <Textarea ref="contentTextarea" v-model="newContent" required class="h-full" fluid />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center w-full gap-2 pt-4">
        <Button label="Проверить" icon="pi pi-book" @click="() => preRenderPrompt('preview')" />

        <div class="grow"></div>

        <Button label="Отмена" class="btn-cancel" icon="pi pi-times" text @click="hideDialog" />
        <Button label="Сохранить" class="btn-primary" icon="pi pi-check" @click="savePrompt" />
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="preRenderedPromptDialogOpen"
    :style="{ width: '50vw' }"
    header="Результат"
    :closeOnEscape="false"
    :modal="true"
  >
    <div class="flex flex-col gap-6">
      <pre>{{ preRenderedPrompt }}</pre>
    </div>

    <template #footer>
      <Button
        label="Закрыть"
        icon="pi pi-check"
        @click="() => (preRenderedPromptDialogOpen = false)"
      />
    </template>
  </Dialog>
</template>

<style scoped></style>
